import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatPrice } from "../lib/api";
import { useCart } from "../lib/cart";
import { useLanguage } from "../lib/language";
import { toast } from "sonner";
import { ShoppingBag, Package } from "lucide-react";

// Build the backend image-proxy URL for a Discord CDN URL (keeps the <img>
// alive past Discord's 24h token expiry).
const proxyImage = (url) => {
    if (!url) return "";
    try {
        const base = api.defaults?.baseURL || "";
        return `${base}/bridge/image?u=${encodeURIComponent(url)}`;
    } catch {
        return url;
    }
};

// Translate the raw bridge payload into the shape the shared cart expects.
// Fields the Discord inventory doesn't carry (vendedor, images[]) are set to
// safe defaults so the rest of the page stays identical.
const normalizeBridge = (p) => ({
    sku: String(p.id || p.sku || "").trim(),
    nombre: p.nombre || "",
    descripcion: p.descripcion || "",
    categoria: p.categoria || "",
    condicion: p.condicion || "Nuevo",
    precio_venta: Number(p.precio_venta || 0),
    cantidad_disponible: Number(p.cantidad_disponible || 0),
    available: Number(p.cantidad_disponible || 0),
    estado: p.estado || (Number(p.cantidad_disponible || 0) > 0 ? "Disponible" : "Vendido"),
    foto: proxyImage(p.foto),
    foto_raw: p.foto || "",
    images: [],
    sold_count: Number(p.sold_count || 0),
    vendedor_id: null,
});

export default function ProductDetail() {
    const { sku } = useParams();
    const [p, setP] = useState(null);
    const [qty, setQty] = useState(1);
    const { add } = useCart();
    const { t } = useLanguage();
    const nav = useNavigate();

    useEffect(() => {
        setP(null);
        api.get(`/bridge/producto/${encodeURIComponent(sku)}`)
            .then(({ data }) => setP(normalizeBridge(data)))
            .catch(() => setP(false));
    }, [sku]);

    if (p === null) return <div className="p-12 text-center">{t("Cargando...", "Loading...")}</div>;
    if (p === false) return <div className="p-12 text-center">{t("Producto no encontrado", "Product not found")}</div>;

    const available = p.cantidad_disponible || 0;
    const out = p.estado === "Vendido" || available === 0;

    const trState = (s) => ({
        "Disponible": "Available",
        "Vendido": "Sold",
    }[s] || s);
    const trCondition = (c) => ({
        "Nuevo": "New",
        "Como nuevo": "Like new",
        "Usado": "Used",
    }[c] || c);

    return (
        <div className="max-w-6xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-12 fade-in">
            <div className="tb-card overflow-hidden">
                {p.foto ? (
                    <img src={p.foto} alt={p.nombre} className="w-full h-full object-cover aspect-[4/5]" />
                ) : (
                    <div className="aspect-[4/5] flex items-center justify-center text-[var(--tb-muted)]">
                        {t("Sin imagen", "No image")}
                    </div>
                )}
            </div>
            <div>
                <div className="flex gap-2 mb-4 flex-wrap">
                    <span className={`tb-badge ${p.condicion === "Nuevo" ? "tb-badge-gold" : "tb-badge-warm"}`}>
                        {t(p.condicion, trCondition(p.condicion))}
                    </span>
                    {p.categoria && <span className="tb-badge tb-badge-muted">{p.categoria}</span>}
                    <span className={`tb-badge ${p.estado === "Disponible" ? "tb-badge-success" : "tb-badge-warning"}`}>
                        {t(p.estado, trState(p.estado))}
                    </span>
                </div>
                <div className="tb-overline text-[var(--tb-muted)] mb-2">SKU · {p.sku}</div>
                <h1 className="font-display text-4xl tracking-tight mb-4">{p.nombre}</h1>
                <div className="mb-6">
                    <div className="text-4xl text-[var(--tb-gold)] font-semibold">
                        {formatPrice(p.precio_venta)} <span className="text-sm text-[var(--tb-text-2)]">USD</span>
                    </div>
                </div>
                <p className="text-[var(--tb-text-2)] leading-relaxed mb-8 whitespace-pre-wrap">{p.descripcion}</p>

                <div className="flex items-center gap-2 text-sm text-[var(--tb-text-2)] mb-6 flex-wrap">
                    <Package className="w-4 h-4" />
                    {available > 0
                        ? (available === 1
                            ? t("1 unidad disponible", "1 unit available")
                            : t(`${available} unidades disponibles`, `${available} units available`))
                        : t("Sin stock disponible", "Out of stock")}
                </div>

                <div className="flex items-center gap-4 mb-6">
                    <label className="tb-overline">{t("Cantidad", "Quantity")}</label>
                    <input
                        type="number"
                        min="1"
                        max={available}
                        value={qty}
                        onChange={(e) => setQty(Math.max(1, parseInt(e.target.value || 1)))}
                        className="tb-input w-24"
                        data-testid="product-qty"
                    />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        disabled={out}
                        onClick={() => { add(p, qty); toast.success(t("Agregado al carrito", "Added to cart")); }}
                        className="tb-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="add-to-cart-button">
                        <ShoppingBag className="w-4 h-4" /> {t("Agregar al carrito", "Add to cart")}
                    </button>
                    <button
                        disabled={out}
                        onClick={() => { add(p, qty); nav("/carrito"); }}
                        className="tb-btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="buy-now-button">
                        {t("Comprar ahora", "Buy now")}
                    </button>
                </div>
                {out && (
                    <div className="mt-4 text-sm text-[var(--tb-warm)]">
                        {t("Producto no disponible. Contáctanos para más información.", "Product unavailable. Contact us for more information.")}
                    </div>
                )}
            </div>
        </div>
    );
}
